import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { YearbookPage, YearbookElement, Position, Size, PAGE_WIDTH, PAGE_HEIGHT } from '@/types/yearbook';
import DraggableElement from './DraggableElement';
import FloatingToolbar from './FloatingToolbar';
import PageActions from './PageActions';
import AddPagesDialog from './AddPagesDialog';
import { Loader2, Type, Plus, Trash2 } from 'lucide-react';
import { GuideLine, computeSnap } from '@/lib/alignmentGuides';
import { toast } from 'sonner';

interface CanvasProps {
  pages: YearbookPage[];
  currentPageIndex: number;
  selectedElementIds: string[];
  loadedIndices: Set<number>;
  onSelectPage: (index: number) => void;
  onSelectElement: (id: string | null, metaKey?: boolean, pageIndex?: number) => void;
  onUpdateElement: (id: string, updates: Partial<YearbookElement>) => void;
  onDragUpdateElement: (id: string, updates: Partial<YearbookElement>) => void;
  onMultiDragUpdate: (updates: Array<{ id: string; position: Position }>) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropImage: (url: string, ratio: number | undefined, position: { x: number; y: number } | null, slotId: string | null, pageIndex: number, pictureId?: number, isSticker?: boolean, thumbUrl?: string) => void;
  onRenamePage?: (pageId: string, name: string) => void;
  onResetPage?: (pageIndex: number) => void;
  onSwapPages?: (indexA: number, indexB: number) => void;
  onDuplicatePageContent?: (sourceIndex: number, targetIndex: number) => void;
  onRemoveBackground?: (pageIndex: number) => void;
  onAddPageGuide?: (pageIndex: number, guide: { orientation: 'horizontal' | 'vertical'; position: number; applyToAll?: boolean }) => void;
  onDeletePageGuide?: (pageIndex: number, guideId: string) => void;
  onRestorePageVersion?: (pageIndex: number, elements: YearbookElement[], background?: import('@/types/yearbook').PageBackground) => void;
  selectedElements: YearbookElement[];
  onDeleteElement: (id: string) => void;
  onDeleteMultiple: (ids: string[]) => void;
  onDuplicateElement: (id: string) => void;
  onDuplicateMultiple: (ids: string[]) => void;
  onMoveLayer: (id: string, dir: 'up' | 'down') => void;
  zoomMultiplier: number;
  onCoverPageClick?: () => void;
  yearbookQuantity?: number;
  coverName?: string;
  defaultFont?: string;
  lockedPageNumbers?: Set<number>;
}

const PAGE_GAP = 24;
const SPREAD_GAP = 4;
const VERTICAL_PADDING = 32;

/**
 * Build spreads: first page alone, then pairs, last page alone.
 */
function buildSpreads(pages: YearbookPage[]): { pages: YearbookPage[]; indices: number[]; isCover?: boolean }[] {
  if (pages.length === 0) return [];
  const spreads: { pages: YearbookPage[]; indices: number[]; isCover?: boolean }[] = [];

  // Find cover pages at the start: optional back, optional tranche, optional front (in this order from API)
  let startIdx = 0;
  const coverPages: YearbookPage[] = [];
  const coverIndices: number[] = [];
  while (
    startIdx < pages.length &&
    (pages[startIdx].coverType === 'back' ||
      pages[startIdx].coverType === 'tranche' ||
      pages[startIdx].coverType === 'front')
  ) {
    coverPages.push(pages[startIdx]);
    coverIndices.push(startIdx);
    startIdx++;
  }
  if (coverPages.length > 0) {
    spreads.push({ pages: coverPages, indices: coverIndices, isCover: true });
  }

  const inner = pages.slice(startIdx);
  if (inner.length === 0) return spreads;

  // First inner page alone
  spreads.push({ pages: [inner[0]], indices: [startIdx] });

  // Middle pages in pairs
  for (let i = 1; i < inner.length - 1; i += 2) {
    if (i + 1 < inner.length - 1) {
      spreads.push({ pages: [inner[i], inner[i + 1]], indices: [startIdx + i, startIdx + i + 1] });
    } else {
      spreads.push({ pages: [inner[i]], indices: [startIdx + i] });
    }
  }

  // Last inner page alone (if more than 1 inner page)
  if (inner.length > 1) {
    const lastIdx = startIdx + inner.length - 1;
    const alreadyAdded = spreads.some((s) => s.indices.includes(lastIdx));
    if (!alreadyAdded) {
      spreads.push({ pages: [pages[lastIdx]], indices: [lastIdx] });
    }
  }

  return spreads;
}

const Canvas: React.FC<CanvasProps> = ({
  pages,
  currentPageIndex,
  selectedElementIds,
  loadedIndices,
  onSelectPage,
  onSelectElement,
  onUpdateElement,
  onDragUpdateElement,
  onMultiDragUpdate,
  onDragStart,
  onDragEnd,
  onDropImage,
  onRenamePage,
  onResetPage,
  onSwapPages,
  onDuplicatePageContent,
  onRemoveBackground,
  onAddPageGuide,
  onDeletePageGuide,
  onRestorePageVersion,
  selectedElements,
  onDeleteElement,
  onDeleteMultiple,
  onDuplicateElement,
  onDuplicateMultiple,
  onMoveLayer,
  zoomMultiplier,
  onCoverPageClick,
  yearbookQuantity,
  coverName,
  defaultFont = 'DM Sans',
  lockedPageNumbers,
}) => {
  const isPageLocked = useCallback(
    (pageNumber: number) => !!lockedPageNumbers && lockedPageNumbers.has(pageNumber),
    [lockedPageNumbers]
  );

  // Clear selection whenever the user lands on a locked page
  useEffect(() => {
    const cur = pages[currentPageIndex];
    if (cur && isPageLocked(cur.pageNumber) && selectedElementIds.length > 0) {
      onSelectElement(null);
    }
  }, [currentPageIndex, pages, isPageLocked, selectedElementIds.length, onSelectElement]);
  const containerRef = useRef<HTMLDivElement>(null);
  const spreadRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [baseScale, setBaseScale] = useState(1);
  const scale = baseScale * zoomMultiplier;
  const [activeGuides, setActiveGuides] = useState<GuideLine[]>([]);
  const [guidesPageIndex, setGuidesPageIndex] = useState<number | null>(null);
  const [guidesOpacity, setGuidesOpacity] = useState(1);
  const keyboardGuideTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const [toolbarPos, setToolbarPos] = useState<{ top: number; left: number } | null>(null);
  const isScrollingToPage = useRef(true);
  const isUserScrolling = useRef(false);
  const [addPagesOpen, setAddPagesOpen] = useState(false);
  const [addPagesAfter, setAddPagesAfter] = useState<number>(1);
  const scrollToTarget = useRef<number | null>(null);
  const currentPageIndexRef = useRef(currentPageIndex);
  currentPageIndexRef.current = currentPageIndex;
  const isMounted = useRef(false);
  const lastProgrammaticPageRef = useRef(currentPageIndex);

  // Marquee selection state
  const [marquee, setMarquee] = useState<{ startX: number; startY: number; endX: number; endY: number; pageIndex: number } | null>(null);
  const marqueePageRef = useRef<HTMLDivElement | null>(null);

  // Selected guide (for delete toolbar)
  const [selectedGuide, setSelectedGuide] = useState<{ pageIndex: number; guideId: string } | null>(null);
  const [cropCounter, setCropCounter] = useState(0);
  const [cropTargetId, setCropTargetId] = useState<string | null>(null);
  const [slotSwapSourceId, setSlotSwapSourceId] = useState<string | null>(null);

  const handleStartCrop = useCallback((id: string) => {
    setCropTargetId(id);
    setCropCounter(c => c + 1);
  }, []);

  const handleStartSlotSwap = useCallback((sourceId: string) => {
    setSlotSwapSourceId(sourceId);
    onSelectElement(null);
    toast.info('Cliquez sur le slot de destination', { duration: 5000 });
  }, [onSelectElement]);

  // Multi-drag: capture initial positions of all selected elements
  const multiDragInitRef = useRef<Map<string, Position>>(new Map());

  const selectedElementId = selectedElementIds.length > 0 ? selectedElementIds[selectedElementIds.length - 1] : null;
  const selectedElement = selectedElements.length > 0 ? selectedElements[selectedElements.length - 1] : null;

  const spreads = useMemo(() => buildSpreads(pages), [pages]);

  // Compute scale so a 2-page spread fits entirely in the visible area
  const computeScale = useCallback(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const padX = 80;
    const padY = VERTICAL_PADDING * 2 + 40;
    const availableW = container.clientWidth - padX;
    const availableH = container.clientHeight - padY;
    const spreadWidth = PAGE_WIDTH * 2 + SPREAD_GAP;
    const s = Math.min(availableW / spreadWidth, availableH / PAGE_HEIGHT, 1);
    setBaseScale(Math.max(0.3, s));
  }, []);

  useEffect(() => {
    computeScale();
    const observer = new ResizeObserver(computeScale);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [computeScale]);

  // Arrow keys to move selected element
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (selectedElements.length === 0) return;
      // Don't intercept if user is typing in an input/textarea/contenteditable
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;

      const movableTypes = ['text', 'image', 'shape', 'shape_border'];
      // Check all selected elements are movable
      const movableEls = selectedElements.filter(el => movableTypes.includes(el.type) && !el.locked);
      if (movableEls.length === 0) return;

      const step = e.shiftKey ? 10 : 1;
      let dx = 0, dy = 0;
      if (e.key === 'ArrowLeft') dx = -step;
      else if (e.key === 'ArrowRight') dx = step;
      else if (e.key === 'ArrowUp') dy = -step;
      else if (e.key === 'ArrowDown') dy = step;
      else return;

      e.preventDefault();

      // Move all selected elements
      for (const el of movableEls) {
        const newX = Math.round(Math.max(0, Math.min(PAGE_WIDTH - el.size.width, el.position.x + dx)));
        const newY = Math.round(Math.max(0, Math.min(PAGE_HEIGHT - el.size.height, el.position.y + dy)));
        onUpdateElement(el.id, { position: { x: newX, y: newY } });
      }

      // Only show guides for single selection
      if (movableEls.length === 1) {
        const el = movableEls[0];
        const newX = Math.round(Math.max(0, Math.min(PAGE_WIDTH - el.size.width, el.position.x + dx)));
        const newY = Math.round(Math.max(0, Math.min(PAGE_HEIGHT - el.size.height, el.position.y + dy)));
        const currentPage = pages[currentPageIndex];
        const siblings = currentPage.elements.filter(s => s.id !== el.id);
        const snap = computeSnap(newX, newY, el.size.width, el.size.height, siblings, PAGE_WIDTH, PAGE_HEIGHT, currentPage.guides ?? []);

        if (e.shiftKey) {
          onUpdateElement(el.id, { position: { x: snap.x, y: snap.y } });
          setActiveGuides(snap.guides);
        } else {
          const isExact = snap.x === newX && snap.y === newY;
          setActiveGuides(isExact ? snap.guides : []);
        }
        setGuidesPageIndex(currentPageIndex);
        setGuidesOpacity(1);

        if (keyboardGuideTimer.current) clearTimeout(keyboardGuideTimer.current);
        keyboardGuideTimer.current = setTimeout(() => {
          setGuidesOpacity(0);
          setTimeout(() => { setActiveGuides([]); setGuidesPageIndex(null); }, 300);
        }, 500);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedElements, onUpdateElement, pages, currentPageIndex]);

  const onSelectPageRef = useRef(onSelectPage);
  onSelectPageRef.current = onSelectPage;
  const spreadsRef = useRef(spreads);
  spreadsRef.current = spreads;

  // Track user scroll to distinguish from programmatic scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let scrollTimer: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      if (!isScrollingToPage.current) {
        isUserScrolling.current = true;
      }
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        isUserScrolling.current = false;
      }, 150);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer);
    };
  }, []);

  // IntersectionObserver for auto-selecting current page on scroll
  useEffect(() => {
    if (!containerRef.current) return;

    const visibilityMap = new Map<number, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingToPage.current) return;

        entries.forEach((entry) => {
          const idx = Number((entry.target as HTMLElement).dataset.spreadIndex);
          if (!isNaN(idx)) {
            visibilityMap.set(idx, entry.intersectionRatio);
          }
        });

        let maxRatio = 0;
        let bestSpreadIdx = 0;
        visibilityMap.forEach((ratio, idx) => {
          if (ratio > maxRatio) {
            maxRatio = ratio;
            bestSpreadIdx = idx;
          }
        });

        const spread = spreadsRef.current[bestSpreadIdx];
        if (spread) {
          const firstPageIdx = spread.indices[0];
          // Don't override if the programmatic target is currently visible
          const programmaticSpread = spreadsRef.current.find(s => s.indices.includes(lastProgrammaticPageRef.current));
          if (programmaticSpread) {
            const progIdx = spreadsRef.current.indexOf(programmaticSpread);
            const progRatio = visibilityMap.get(progIdx) ?? 0;
            // Target is still visible and is NOT the most visible spread — don't switch
            if (progRatio > 0.2 && progIdx !== bestSpreadIdx) return;
          }
          if (!spread.indices.includes(currentPageIndexRef.current)) {
            onSelectPageRef.current(firstPageIdx);
          }
        }
      },
      {
        root: containerRef.current,
        threshold: [0, 0.25, 0.5, 0.75, 1.0],
      }
    );

    spreadRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [pages.length]);

  // Scroll to the spread containing currentPageIndex — only for programmatic navigation (dropdown, etc.)
  useEffect(() => {
    const isFirstRender = !isMounted.current;
    if (isFirstRender) {
      isMounted.current = true;
    }
    // Skip if this change came from the user scrolling (IntersectionObserver)
    if (isUserScrolling.current) return;

    lastProgrammaticPageRef.current = currentPageIndex;

    const doScroll = () => {
      const spreadIdx = spreads.findIndex((s) => s.indices.includes(currentPageIndex));
      const el = spreadRefs.current[spreadIdx];
      if (el && containerRef.current) {
        const container = containerRef.current;
        const elRect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const needsScroll = elRect.top < containerRect.top || elRect.bottom > containerRect.bottom;
        if (needsScroll || isFirstRender) {
          isScrollingToPage.current = true;
          el.scrollIntoView({ behavior: isFirstRender ? 'instant' : 'smooth', block: 'center' });
          // Use longer guard on first render (layout may still shift)
          const guardMs = isFirstRender ? 1000 : 800;
          setTimeout(() => { isScrollingToPage.current = false; }, guardMs);
        } else {
          // No scroll needed — unlock the observer guard
          isScrollingToPage.current = false;
        }
      } else {
        // Element not found — retry once after a frame (DOM may not be ready yet on mount)
        if (isFirstRender) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const retryIdx = spreads.findIndex((s) => s.indices.includes(currentPageIndex));
              const retryEl = spreadRefs.current[retryIdx];
              if (retryEl) {
                isScrollingToPage.current = true;
                retryEl.scrollIntoView({ behavior: 'instant', block: 'center' });
                setTimeout(() => { isScrollingToPage.current = false; }, 1000);
              } else {
                isScrollingToPage.current = false;
              }
            });
          });
        } else {
          isScrollingToPage.current = false;
        }
      }
    };

    // On first render, delay to ensure layout is fully computed
    if (isFirstRender) {
      requestAnimationFrame(() => {
        requestAnimationFrame(doScroll);
      });
    } else {
      doScroll();
    }
  }, [currentPageIndex, spreads]);

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget || (e.target as HTMLElement).dataset.canvas) {
        if (slotSwapSourceId) {
          setSlotSwapSourceId(null);
          toast.dismiss();
        }
        onSelectElement(null);
        setSelectedGuide(null);
      }
    },
    [onSelectElement, slotSwapSourceId]
  );

  const handlePageClick = useCallback(
    (pageIndex: number, e?: React.MouseEvent) => {
      // Ignore clicks that landed on an element — element selection handles the tab switch itself
      if (e && (e.target as HTMLElement).closest('[data-element-id]')) return;
      const page = pages[pageIndex];
      const isCover = !!page?.coverType;
      if (pageIndex !== currentPageIndex) {
        onSelectPage(pageIndex);
        onSelectElement(null);
      }
      setSelectedGuide(null);
      if (isCover) {
        onCoverPageClick?.();
      }
    },
    [currentPageIndex, onSelectPage, onSelectElement, pages, onCoverPageClick]
  );

  const handleElementSelect = useCallback(
    (elementId: string, pageIndex?: number, metaKey?: boolean) => {
      const targetPage = pages[pageIndex ?? currentPageIndex];
      if (targetPage && isPageLocked(targetPage.pageNumber)) {
        // Locked page: ignore element interactions entirely
        return;
      }
      // Handle slot swap mode
      if (slotSwapSourceId) {
        const page = pages[pageIndex ?? currentPageIndex];
        const targetEl = page?.elements.find(e => e.id === elementId);
        const sourceEl = pages[currentPageIndex]?.elements.find(e => e.id === slotSwapSourceId);
        
        if (targetEl && sourceEl && targetEl.type === 'slot' && targetEl.id !== slotSwapSourceId) {
          // Swap or move images between slots
          const sourceImageProps = {
            imageUrl: sourceEl.imageUrl,
            thumbnail: sourceEl.thumbnail,
            pictureId: sourceEl.pictureId,
            imageRatio: sourceEl.imageRatio,
            imageOffset: sourceEl.imageOffset,
            imageScale: sourceEl.imageScale,
          };
          const targetImageProps = {
            imageUrl: targetEl.imageUrl,
            thumbnail: targetEl.thumbnail,
            pictureId: targetEl.pictureId,
            imageRatio: targetEl.imageRatio,
            imageOffset: targetEl.imageOffset,
            imageScale: targetEl.imageScale,
          };

          // Put source image into target
          onUpdateElement(targetEl.id, sourceImageProps);
          // Put target image (or clear) into source
          onUpdateElement(slotSwapSourceId, targetEl.imageUrl
            ? targetImageProps
            : { imageUrl: undefined, thumbnail: undefined, pictureId: undefined, imageRatio: undefined, imageOffset: undefined, imageScale: undefined }
          );

          setSlotSwapSourceId(null);
          toast.dismiss();
          toast.success(targetEl.imageUrl ? 'Photos échangées' : 'Photo déplacée');
          return;
        } else if (!targetEl || targetEl.type !== 'slot') {
          toast.error('Veuillez cliquer sur un slot');
          return;
        }
        // Clicked same slot — cancel
        setSlotSwapSourceId(null);
        toast.dismiss();
        return;
      }

      if (pageIndex !== undefined && pageIndex !== currentPageIndex) {
        onSelectPage(pageIndex);
      }
      // Clic sur un slot vide : si aucune photo n'existe, ouvrir la popup d'import
      const clickedEl = targetPage?.elements.find((el) => el.id === elementId);
      if (
        clickedEl &&
        (clickedEl.type === 'slot' || clickedEl.type === 'free_slot') &&
        !clickedEl.imageUrl
      ) {
        window.dispatchEvent(new CustomEvent('yearbook:open-import-if-empty'));
      }
      onSelectElement(elementId, metaKey, pageIndex ?? currentPageIndex);
      setSelectedGuide(null);
    },
    [currentPageIndex, onSelectPage, onSelectElement, slotSwapSourceId, pages, onUpdateElement, isPageLocked]
  );

  // Multi-drag handlers
  const handleMultiDragStart = useCallback((ids?: string[]) => {
    onDragStart();
    const page = pages[currentPageIndex];
    const map = new Map<string, Position>();
    for (const id of (ids && ids.length > 1 ? ids : selectedElementIds)) {
      const el = page?.elements.find((e) => e.id === id);
      if (el) map.set(id, { ...el.position });
    }
    multiDragInitRef.current = map;
  }, [onDragStart, pages, currentPageIndex, selectedElementIds]);

  const handleMultiDragUpdate = useCallback((dx: number, dy: number) => {
    const initials = multiDragInitRef.current;
    const updates: Array<{ id: string; position: Position }> = [];
    initials.forEach((initPos, id) => {
      updates.push({ id, position: { x: Math.round(initPos.x + dx), y: Math.round(initPos.y + dy) } });
    });
    onMultiDragUpdate(updates);
  }, [onMultiDragUpdate]);

  // ===== Group resize via bounding box handles =====
  const groupResizeRef = useRef<{
    corner: string;
    anchorX: number;
    anchorY: number;
    initBboxW: number;
    initBboxH: number;
    startClientX: number;
    startClientY: number;
    initials: Map<string, { pos: Position; size: Size }>;
  } | null>(null);
  const [isGroupResizing, setIsGroupResizing] = useState(false);

  const handleGroupResizeMouseDown = useCallback((e: React.MouseEvent, corner: string) => {
    e.stopPropagation();
    e.preventDefault();
    if (selectedElements.length < 2) return;

    onDragStart();

    // Compute bounding box in page coords
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const initials = new Map<string, { pos: Position; size: Size }>();
    for (const el of selectedElements) {
      initials.set(el.id, { pos: { ...el.position }, size: { ...el.size } });
      if (el.position.x < minX) minX = el.position.x;
      if (el.position.y < minY) minY = el.position.y;
      if (el.position.x + el.size.width > maxX) maxX = el.position.x + el.size.width;
      if (el.position.y + el.size.height > maxY) maxY = el.position.y + el.size.height;
    }

    // Anchor = opposite corner
    const anchorX = corner.includes('l') ? maxX : minX;
    const anchorY = corner.includes('t') ? maxY : minY;

    groupResizeRef.current = {
      corner,
      anchorX,
      anchorY,
      initBboxW: maxX - minX,
      initBboxH: maxY - minY,
      startClientX: e.clientX,
      startClientY: e.clientY,
      initials,
    };
    setIsGroupResizing(true);
  }, [selectedElements, onDragStart]);

  useEffect(() => {
    if (!isGroupResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      const gr = groupResizeRef.current;
      if (!gr) return;
      const dx = (e.clientX - gr.startClientX) / scale;
      const dy = (e.clientY - gr.startClientY) / scale;

      // Compute new bbox dimensions based on corner
      let newBboxW = gr.initBboxW;
      let newBboxH = gr.initBboxH;
      if (gr.corner.includes('r')) newBboxW = gr.initBboxW + dx;
      if (gr.corner.includes('l')) newBboxW = gr.initBboxW - dx;
      if (gr.corner.includes('b')) newBboxH = gr.initBboxH + dy;
      if (gr.corner.includes('t')) newBboxH = gr.initBboxH - dy;

      // Proportional: use the dominant axis
      const ratioW = newBboxW / gr.initBboxW;
      const ratioH = newBboxH / gr.initBboxH;
      const ratio = Math.max(0.1, Math.abs(ratioW) > Math.abs(ratioH) ? ratioW : ratioH);

      gr.initials.forEach(({ pos, size }, id) => {
        const newW = Math.max(20, Math.round(size.width * ratio));
        const newH = Math.max(20, Math.round(size.height * ratio));
        const newX = Math.round(gr.anchorX + (pos.x - gr.anchorX) * ratio);
        const newY = Math.round(gr.anchorY + (pos.y - gr.anchorY) * ratio);
        onUpdateElement(id, { size: { width: newW, height: newH }, position: { x: newX, y: newY } });
      });
    };

    const handleMouseUp = () => {
      setIsGroupResizing(false);
      groupResizeRef.current = null;
      onDragEnd();
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isGroupResizing, scale, onUpdateElement, onDragEnd]);

  // Compute group bounding box for rendering
  const groupBbox = useMemo(() => {
    if (selectedElements.length < 2) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const el of selectedElements) {
      if (el.position.x < minX) minX = el.position.x;
      if (el.position.y < minY) minY = el.position.y;
      if (el.position.x + el.size.width > maxX) maxX = el.position.x + el.size.width;
      if (el.position.y + el.size.height > maxY) maxY = el.position.y + el.size.height;
    }
    if (minX === Infinity) return null;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }, [selectedElements]);
  const handlePageMouseDown = useCallback((e: React.MouseEvent, pageIndex: number) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    // Only start marquee if clicking directly on the page background
    if (!target.dataset.canvas && target !== e.currentTarget) return;

    const pageEl = e.currentTarget as HTMLDivElement;
    const rect = pageEl.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    marqueePageRef.current = pageEl;
    setMarquee({ startX: x, startY: y, endX: x, endY: y, pageIndex });
  }, [scale]);

  // Marquee mouse move/up
  useEffect(() => {
    if (!marquee) return;

    const handleMouseMove = (e: MouseEvent) => {
      const pageEl = marqueePageRef.current;
      if (!pageEl) return;
      const rect = pageEl.getBoundingClientRect();
      const x = (e.clientX - rect.left) / scale;
      const y = (e.clientY - rect.top) / scale;
      setMarquee((prev) => prev ? { ...prev, endX: x, endY: y } : null);
    };

    const handleMouseUp = () => {
      if (!marquee) { setMarquee(null); return; }

      const left = Math.min(marquee.startX, marquee.endX);
      const top = Math.min(marquee.startY, marquee.endY);
      const right = Math.max(marquee.startX, marquee.endX);
      const bottom = Math.max(marquee.startY, marquee.endY);
      const width = right - left;
      const height = bottom - top;

      if (width > 5 && height > 5) {
        const page = pages[marquee.pageIndex];
        const hitIds = page.elements
          .filter((el) => el.type !== 'slot') // Skip slots
          .filter((el) => {
            return !(el.position.x + el.size.width < left ||
                     el.position.x > right ||
                     el.position.y + el.size.height < top ||
                     el.position.y > bottom);
          })
          .map((el) => el.id);

        if (hitIds.length > 0) {
          if (marquee.pageIndex !== currentPageIndex) {
            onSelectPage(marquee.pageIndex);
          }
          // Replace selection with marquee selection
          onSelectElement(null); // clear first
          // Set all hit ids — use a small timeout to ensure clear happened
          setTimeout(() => {
            for (const id of hitIds) {
              onSelectElement(id, true); // metaKey=true to add to selection
            }
          }, 0);
        }
      } else {
        // Tiny drag = click on background → deselect
        onSelectElement(null);
      }

      setMarquee(null);
      marqueePageRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [marquee, pages, currentPageIndex, onSelectPage, onSelectElement, scale]);

  // Stable callback for guide changes from DraggableElement
  const handleGuidesChange = useCallback((guides: GuideLine[]) => {
    setActiveGuides(guides);
    setGuidesPageIndex((prev) => guides.length > 0 ? currentPageIndex : null);
    setGuidesOpacity(1);
  }, [currentPageIndex]);

  const renderPage = (page: YearbookPage, pageIndex: number) => {
    const isCurrentPage = pageIndex === currentPageIndex;
    const isLoaded = loadedIndices.has(pageIndex);
    const pageW = page.pageWidth ?? PAGE_WIDTH;

    // Spinner for unloaded pages
    if (!isLoaded) {
      return (
        <div
          key={page.id}
          className="flex flex-col items-center"
        >
          <div
            className="relative shrink-0 flex items-center justify-center rounded-sm bg-white editor-shadow"
            style={{
              width: pageW * scale,
              height: PAGE_HEIGHT * scale,
            }}
          >
            <Loader2
              className="animate-spin text-muted-foreground"
              size={32}
            />
          </div>
          <PageActions page={page} pageIndex={pageIndex} scale={scale} coverName={coverName} onRename={onRenamePage} onReset={onResetPage} onSwapPages={onSwapPages} onDuplicatePageContent={onDuplicatePageContent} onRemoveBackground={onRemoveBackground} onAddGuide={page.coverType !== 'tranche' ? onAddPageGuide : undefined} onRestoreVersion={onRestorePageVersion} pages={pages} />
        </div>
      );
    }

    // Background bleed
    const BG_BLEED = 14.1732;
    const bgIsImage = page.background.type !== 'color';
    const hasUserBg = bgIsImage && (page.background.trame_background_id != null || page.background.trame_background_custom_id != null);
    const bgInset = hasUserBg ? BG_BLEED : 0;
    const bgStyle: React.CSSProperties = bgIsImage
      ? {}
      : { backgroundColor: page.background.value };

    // Marquee rect for this page
    const pageMarquee = marquee && marquee.pageIndex === pageIndex ? marquee : null;
    const marqueeRect = pageMarquee ? {
      left: Math.min(pageMarquee.startX, pageMarquee.endX),
      top: Math.min(pageMarquee.startY, pageMarquee.endY),
      width: Math.abs(pageMarquee.endX - pageMarquee.startX),
      height: Math.abs(pageMarquee.endY - pageMarquee.startY),
    } : null;

    return (
      <div
        key={page.id}
        className="flex flex-col items-center"
      >
        <div className="text-xs text-muted-foreground font-medium mb-3 truncate max-w-[200px] min-h-[1rem]" title={page.name || ''}>
          {page.name || '\u00A0'}
        </div>

        <div
          className="relative shrink-0"
          style={{
            width: pageW * scale,
            height: PAGE_HEIGHT * scale,
          }}
        >
          <div
            className={`absolute -inset-1 rounded-sm transition-all pointer-events-none z-10 ${
              isCurrentPage
                ? 'ring-2 ring-editor-sidebar-active ring-offset-2 ring-offset-editor-canvas'
                : ''
            }`}
          />

          <div
            data-page-id={page.id}
            className="absolute inset-0 editor-shadow rounded-sm overflow-hidden origin-top-left"
            style={{
              width: pageW,
              height: PAGE_HEIGHT,
              transform: `scale(${scale})`,
              ...bgStyle,
            }}
            onClick={(e) => handlePageClick(pageIndex, e)}
            onMouseDown={(e) => handlePageMouseDown(e, pageIndex)}
            data-canvas="true"
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes('application/yearbook-image') || e.dataTransfer.types.includes('application/yearbook-sticker')) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }
            }}
            onDrop={(e) => {
              e.preventDefault();

              const stickerRaw = e.dataTransfer.getData('application/yearbook-sticker');
              if (stickerRaw) {
                const { url, width, height } = JSON.parse(stickerRaw);
                const rect = e.currentTarget.getBoundingClientRect();
                const x = (e.clientX - rect.left) / scale;
                const y = (e.clientY - rect.top) / scale;
                onDropImage(url, width / height, { x, y }, null, pageIndex, undefined, true);
                return;
              }

              const raw = e.dataTransfer.getData('application/yearbook-image');
              if (!raw) return;
              const { url, ratio, pictureId, thumbUrl } = JSON.parse(raw);

              const rect = e.currentTarget.getBoundingClientRect();
              const x = (e.clientX - rect.left) / scale;
              const y = (e.clientY - rect.top) / scale;

              const hitElement = page.elements.find(
                (el) =>
                  (el.type === 'slot' || el.type === 'free_slot' || (el.type === 'image' && !el.isSticker && !el.qrcodeId)) &&
                  x >= el.position.x &&
                  x <= el.position.x + el.size.width &&
                  y >= el.position.y &&
                  y <= el.position.y + el.size.height
              );

              if (hitElement) {
                onDropImage(url, ratio, null, hitElement.id, pageIndex, pictureId, undefined, thumbUrl);
              } else {
                onDropImage(url, ratio, { x: Math.round(x - 100), y: Math.round(y - 75) }, null, pageIndex, pictureId, undefined, thumbUrl);
              }
            }}
          >
            {bgIsImage && (
              <img
                src={page.background.value}
                alt=""
                className="absolute pointer-events-none"
                style={{
                  top: -bgInset,
                  left: -bgInset,
                  width: pageW + bgInset * 2,
                  height: PAGE_HEIGHT + bgInset * 2,
                  maxWidth: 'none',
                  objectFit: 'cover',
                }}
                draggable={false}
              />
            )}

            {page.elements.map((element, idx) => {
              const isSlot = element.type === 'slot';
              const isFreeSlot = element.type === 'free_slot';
              const isDraggable = !isSlot && !element.locked && !(page.itemType === 'Trombi' && element.type === 'text');
              const isInMulti = selectedElementIds.length > 1 && selectedElementIds.includes(element.id);
              const groupDragIds = element.groupId
                ? page.elements.filter((e) => e.groupId === element.groupId).map((e) => e.id)
                : undefined;
              const dragsAsGroup = isInMulti || (!!groupDragIds && groupDragIds.length > 1);
              return (
                <DraggableElement
                  key={element.id}
                  element={element}
                  isSelected={element.id === selectedElementId && isCurrentPage}
                  isInMultiSelection={isInMulti && isCurrentPage}
                  onSelect={handleElementSelect}
                  onUpdate={onUpdateElement}
                  onDragUpdate={onDragUpdateElement}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  groupDragIds={groupDragIds}
                  onMultiDragStart={dragsAsGroup ? handleMultiDragStart : undefined}
                  onMultiDragUpdate={dragsAsGroup ? handleMultiDragUpdate : undefined}
                  scale={scale}
                  isTrombiPage={page.itemType === 'Trombi'}
                  allElements={isDraggable && !dragsAsGroup ? page.elements : undefined}
                  onGuidesChange={isDraggable && !dragsAsGroup ? handleGuidesChange : undefined}
                  elementIndex={idx}
                  pageIndex={pageIndex}
                  pageGuides={page.guides}
                  forceCropId={cropTargetId === element.id ? cropCounter : 0}
                />
              );
            })}

            {/* Slot swap mode: highlight all slots on current page */}
            {slotSwapSourceId && isCurrentPage && page.elements
              .filter(el => el.type === 'slot' && el.id !== slotSwapSourceId)
              .map(el => (
                <div
                  key={`swap-highlight-${el.id}`}
                  className="absolute pointer-events-none rounded-sm"
                  style={{
                    left: el.position.x,
                    top: el.position.y,
                    width: el.size.width,
                    height: el.size.height,
                    border: '2px dashed hsl(var(--primary))',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    zIndex: 998,
                  }}
                />
              ))
            }

            {isCurrentPage && groupBbox && selectedElements.length >= 2 && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: groupBbox.x,
                  top: groupBbox.y,
                  width: groupBbox.width,
                  height: groupBbox.height,
                  border: '1.5px dashed hsl(var(--editor-sidebar-active))',
                  zIndex: 999,
                }}
              >
                {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => {
                  const style: React.CSSProperties = {
                    position: 'absolute',
                    width: 10,
                    height: 10,
                    backgroundColor: 'hsl(var(--background))',
                    border: '2px solid hsl(var(--editor-sidebar-active))',
                    borderRadius: 2,
                    pointerEvents: 'auto',
                    zIndex: 1000,
                    cursor: corner === 'tl' || corner === 'br' ? 'nwse-resize' : 'nesw-resize',
                  };
                  if (corner.includes('t')) style.top = -5;
                  if (corner.includes('b')) style.bottom = -5;
                  if (corner.includes('l')) style.left = -5;
                  if (corner.includes('r')) style.right = -5;
                  return (
                    <div
                      key={corner}
                      style={style}
                      onMouseDown={(e) => handleGroupResizeMouseDown(e, corner)}
                    />
                  );
                })}
              </div>
            )}

            {/* Marquee selection rectangle */}
            {marqueeRect && marqueeRect.width > 2 && (
              <div
                className="absolute pointer-events-none border-2 border-primary/60 bg-primary/10 rounded-sm"
                style={{
                  left: marqueeRect.left,
                  top: marqueeRect.top,
                  width: marqueeRect.width,
                  height: marqueeRect.height,
                }}
              />
            )}

            {/* User-defined page guides (rulers) — editor-only, dashed */}
            {(page.guides ?? []).map((g) => {
              const isSel = selectedGuide?.pageIndex === pageIndex && selectedGuide?.guideId === g.id;
              const color = isSel ? '#f97316' : '#06b6d4';
              return (
                <div
                  key={`pg-${g.id}`}
                  data-guide-id={g.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedGuide({ pageIndex, guideId: g.id });
                    onSelectElement(null);
                  }}
                  className="absolute cursor-pointer"
                  style={
                    g.orientation === 'vertical'
                      ? { left: g.position - 3, top: 0, width: 7, height: PAGE_HEIGHT, zIndex: 998 }
                      : { top: g.position - 3, left: 0, height: 7, width: pageW, zIndex: 998 }
                  }
                >
                  <div
                    style={
                      g.orientation === 'vertical'
                        ? { position: 'absolute', left: 3, top: 0, width: 1, height: '100%', borderLeft: `1px dashed ${color}` }
                        : { position: 'absolute', top: 3, left: 0, height: 1, width: '100%', borderTop: `1px dashed ${color}` }
                    }
                  />
                </div>
              );
            })}

            {guidesPageIndex === pageIndex && activeGuides.map((guide, i) => {
              const isSpacing = guide.type === 'spacing';

              if (isSpacing && guide.from != null && guide.to != null) {
                const color = '#f97316';
                if (guide.orientation === 'vertical') {
                  const rx = Math.min(guide.from, guide.to);
                  const rw = Math.abs(guide.to - guide.from);
                  const ry = guide.crossStart ?? 0;
                  const rh = (guide.crossEnd ?? PAGE_HEIGHT) - ry;
                  return (
                    <svg key={`guide-${i}`} className="absolute inset-0 pointer-events-none" width={PAGE_WIDTH} height={PAGE_HEIGHT} style={{ opacity: guidesOpacity, transition: 'opacity 0.3s ease-out' }}>
                      <rect x={rx} y={ry} width={rw} height={rh} fill={color} fillOpacity={0.2} stroke={color} strokeWidth={1} />
                    </svg>
                  );
                } else {
                  const ry = Math.min(guide.from, guide.to);
                  const rh = Math.abs(guide.to - guide.from);
                  const rx = guide.crossStart ?? 0;
                  const rw = (guide.crossEnd ?? PAGE_WIDTH) - rx;
                  return (
                    <svg key={`guide-${i}`} className="absolute inset-0 pointer-events-none" width={PAGE_WIDTH} height={PAGE_HEIGHT} style={{ opacity: guidesOpacity, transition: 'opacity 0.3s ease-out' }}>
                      <rect x={rx} y={ry} width={rw} height={rh} fill={color} fillOpacity={0.2} stroke={color} strokeWidth={1} />
                    </svg>
                  );
                }
              }

              const color = 'hsl(var(--editor-element-selected) / 0.7)';
              return (
                <div
                  key={`guide-${i}`}
                  className="absolute pointer-events-none"
                  style={{
                    ...(guide.orientation === 'vertical'
                      ? { left: guide.position, top: 0, width: 1, height: PAGE_HEIGHT, backgroundColor: color }
                      : { top: guide.position, left: 0, height: 1, width: PAGE_WIDTH, backgroundColor: color }),
                    opacity: guidesOpacity,
                    transition: 'opacity 0.3s ease-out',
                  }}
                />
              );
            })}

            {/* Locked-page overlay: blocks all interaction and visually greys out */}
            {isPageLocked(page.pageNumber) && (
              <div
                className="absolute inset-0 bg-muted/60 backdrop-grayscale flex items-start justify-center pt-3"
                style={{ zIndex: 2000, cursor: 'not-allowed' }}
                onClickCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onMouseDownCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onPointerDownCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'none'; }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); }}
              >
                <div className="px-2.5 py-1 rounded-full bg-foreground/80 text-background text-[11px] font-medium shadow">
                  Page verrouillée
                </div>
              </div>
            )}
          </div>
        </div>

        <PageActions page={page} pageIndex={pageIndex} scale={scale} coverName={coverName} onRename={onRenamePage} onReset={onResetPage} onSwapPages={onSwapPages} onDuplicatePageContent={onDuplicatePageContent} onRemoveBackground={onRemoveBackground} onAddGuide={page.coverType !== 'tranche' ? onAddPageGuide : undefined} onRestoreVersion={onRestorePageVersion} pages={pages} />
      </div>
    );
  };

  // Compute floating toolbar position
  useEffect(() => {
    if (selectedElements.length === 0 || !containerRef.current) {
      setToolbarPos(null);
      return;
    }
    const updatePos = () => {
      const container = containerRef.current;
      if (!container) return;

      if (selectedElements.length === 1) {
        // Single element
        const elDom = container.querySelector(`[data-element-id="${selectedElements[0].id}"]`) as HTMLElement | null;
        if (!elDom) { setToolbarPos(null); return; }
        const containerRect = container.getBoundingClientRect();
        const elRect = elDom.getBoundingClientRect();
        const top = elRect.top - containerRect.top + container.scrollTop - 76;
        const left = elRect.left - containerRect.left + container.scrollLeft + elRect.width / 2;
        setToolbarPos({ top: Math.max(4, top), left });
      } else {
        // Multi: compute bounding box of all selected elements
        const containerRect = container.getBoundingClientRect();
        let minTop = Infinity, maxBottom = -Infinity, minLeft = Infinity, maxRight = -Infinity;
        for (const el of selectedElements) {
          const elDom = container.querySelector(`[data-element-id="${el.id}"]`) as HTMLElement | null;
          if (!elDom) continue;
          const elRect = elDom.getBoundingClientRect();
          const t = elRect.top - containerRect.top + container.scrollTop;
          const b = t + elRect.height;
          const l = elRect.left - containerRect.left + container.scrollLeft;
          const r = l + elRect.width;
          if (t < minTop) minTop = t;
          if (b > maxBottom) maxBottom = b;
          if (l < minLeft) minLeft = l;
          if (r > maxRight) maxRight = r;
        }
        if (minTop === Infinity) { setToolbarPos(null); return; }
        const top = minTop - 76;
        const left = (minLeft + maxRight) / 2;
        setToolbarPos({ top: Math.max(4, top), left });
      }
    };
    updatePos();
    const container = containerRef.current;
    container.addEventListener('scroll', updatePos, { passive: true });
    const resizeObs = new ResizeObserver(updatePos);
    for (const el of selectedElements) {
      const elDom = container.querySelector(`[data-element-id="${el.id}"]`) as HTMLElement | null;
      if (elDom) resizeObs.observe(elDom);
    }
    return () => {
      container.removeEventListener('scroll', updatePos);
      resizeObs.disconnect();
    };
  }, [selectedElements, selectedElementIds, scale]);

  // Update toolbar position when selected elements move
  useEffect(() => {
    if (selectedElements.length === 0 || !containerRef.current) return;
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();

    if (selectedElements.length === 1) {
      const el = selectedElements[0];
      const elDom = container.querySelector(`[data-element-id="${el.id}"]`) as HTMLElement | null;
      if (!elDom) return;
      const elRect = elDom.getBoundingClientRect();
      const top = elRect.top - containerRect.top + container.scrollTop - 76;
      const left = elRect.left - containerRect.left + container.scrollLeft + elRect.width / 2;
      setToolbarPos({ top: Math.max(4, top), left });
    } else {
      let minTop = Infinity, maxBottom = -Infinity, minLeft = Infinity, maxRight = -Infinity;
      for (const el of selectedElements) {
        const elDom = container.querySelector(`[data-element-id="${el.id}"]`) as HTMLElement | null;
        if (!elDom) continue;
        const elRect = elDom.getBoundingClientRect();
        const t = elRect.top - containerRect.top + container.scrollTop;
        const b = t + elRect.height;
        const l = elRect.left - containerRect.left + container.scrollLeft;
        const r = l + elRect.width;
        if (t < minTop) minTop = t;
        if (b > maxBottom) maxBottom = b;
        if (l < minLeft) minLeft = l;
        if (r > maxRight) maxRight = r;
      }
      if (minTop === Infinity) return;
      const top = minTop - 76;
      const left = (minLeft + maxRight) / 2;
      setToolbarPos({ top: Math.max(4, top), left });
    }
  }, [selectedElements.map(e => `${e.position.x},${e.position.y},${e.size.width},${e.size.height},${e.rotation}`).join('|')]);

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-auto bg-editor-canvas relative"
      style={{ overflowX: zoomMultiplier > 1 ? 'auto' : 'hidden' }}
      onClick={handleCanvasClick}
      data-canvas="true"
    >
      <div
        className="flex flex-col items-center"
        style={{
          padding: `${VERTICAL_PADDING}px 24px`,
          gap: PAGE_GAP,
          minWidth: zoomMultiplier > 1 ? PAGE_WIDTH * 2 * scale + 80 : undefined,
        }}
        data-canvas="true"
      >
        {spreads.map((spread, spreadIdx) => {
          const isSingle = spread.pages.length === 1;
          const isInterior = !spread.isCover;
          const nextSpread = spreads[spreadIdx + 1];
          const showAddButton = isInterior && nextSpread && !nextSpread.isCover;
          const coverCount = pages.filter(p => p.coverType).length;
          const lastPageIdx = spread.indices[spread.indices.length - 1];
          const interiorPageNum = lastPageIdx - coverCount + 1;
          const isLastInteriorSpread = isInterior && (!nextSpread || nextSpread.isCover);
          const showFakePage = isLastInteriorSpread && isSingle;

          return (
            <React.Fragment key={spread.indices.join('-')}>
              <div
                ref={(el) => {
                  spreadRefs.current[spreadIdx] = el;
                }}
                data-spread-index={spreadIdx}
                className="flex flex-col items-center"
              >
                {spread.isCover && (
                  <div className="text-xs text-muted-foreground font-medium mb-2">
                    Couverture
                  </div>
                )}

                <div
                  className="flex items-start"
                  style={{
                    gap: SPREAD_GAP * scale,
                    width: spread.isCover
                      ? spread.pages.reduce((sum, p) => sum + (p.pageWidth ?? PAGE_WIDTH), 0) * scale + SPREAD_GAP * scale * Math.max(0, spread.pages.length - 1)
                      : (isSingle && !showFakePage)
                      ? PAGE_WIDTH * scale
                      : PAGE_WIDTH * 2 * scale + SPREAD_GAP * scale,
                    justifyContent: 'center',
                  }}
                >
                  {spread.pages.map((page, i) =>
                    renderPage(page, spread.indices[i])
                  )}

                  {showFakePage && (
                    <div className="flex flex-col items-center">
                      <div className="text-xs text-muted-foreground font-medium mb-3 min-h-[1rem]">&nbsp;</div>
                      <button
                        onClick={() => {
                          const cCount = pages.filter(p => p.coverType).length;
                          const totalInterior = pages.length - cCount;
                          setAddPagesAfter(totalInterior);
                          setAddPagesOpen(true);
                        }}
                        className="relative shrink-0 flex flex-col items-center justify-center gap-4 rounded-sm border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 bg-muted/20 hover:bg-muted/40 transition-all cursor-pointer group"
                        style={{
                          width: PAGE_WIDTH * scale,
                          height: PAGE_HEIGHT * scale,
                        }}
                      >
                        <div className="w-14 h-14 rounded-full bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-colors">
                          <Plus className="text-primary" size={28} />
                        </div>
                        <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors text-center px-6 leading-snug">
                          Ajouter plus de pages<br />pour aérer mon Yearbook
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {showAddButton && (
                <button
                  onClick={() => {
                    setAddPagesAfter(interiorPageNum);
                    setAddPagesOpen(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/60 transition-colors bg-transparent hover:bg-muted/50"
                >
                  <Plus size={12} />
                  Ajouter des pages
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Floating toolbar for empty slots — convert to text */}
      {selectedElement && toolbarPos && selectedElement.type === 'slot' && !selectedElement.imageUrl && selectedElements.length === 1 && (
        <div
          className="absolute z-50 pointer-events-auto"
          style={{ top: toolbarPos.top, left: toolbarPos.left, transform: 'translateX(-50%)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1 px-1.5 py-1 rounded-lg bg-background/95 backdrop-blur-sm border border-border shadow-lg">
            <button
              onClick={() => {
                const MARGIN_TEXT = 14.1732;
                const maxW = PAGE_WIDTH - 2 * MARGIN_TEXT;
                const maxH = PAGE_HEIGHT - 2 * MARGIN_TEXT;
                const newW = Math.min(selectedElement.size.width, maxW);
                const newH = Math.min(selectedElement.size.height, maxH);
                const newX = Math.max(MARGIN_TEXT, Math.min(selectedElement.position.x, PAGE_WIDTH - MARGIN_TEXT - newW));
                const newY = Math.max(MARGIN_TEXT, Math.min(selectedElement.position.y, PAGE_HEIGHT - MARGIN_TEXT - newH));
                onUpdateElement(selectedElement.id, {
                  type: 'text',
                  content: 'Double-cliquez pour éditer',
                  textStyle: { fontSize: 16, fontFamily: defaultFont, fontWeight: '400', color: '#1a1a2e', textAlign: 'left', italic: false, underline: false },
                  size: { width: newW, height: newH },
                  position: { x: newX, y: newY },
                });
              }}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium bg-background/80 hover:bg-muted transition-colors text-foreground"
            >
              <Type size={14} />
              Convertir en texte
            </button>
          </div>
        </div>
      )}

      {/* Floating toolbar — hidden on tranche page (text is locked, only style edits allowed via sidebar) */}
      {selectedElements.length > 0 && toolbarPos && !(selectedElements.length === 1 && selectedElement?.type === 'slot' && !selectedElement?.imageUrl) && pages[currentPageIndex]?.coverType !== 'tranche' && (
        <FloatingToolbar
          selectedElements={selectedElements}
          position={{ top: toolbarPos.top, left: toolbarPos.left }}
          onDeleteElement={onDeleteElement}
          onDuplicateElement={onDuplicateElement}
          onMoveLayer={onMoveLayer}
          onUpdateElement={onUpdateElement}
          onDeleteMultiple={onDeleteMultiple}
          onDuplicateMultiple={onDuplicateMultiple}
           onStartCrop={handleStartCrop}
           onStartSlotSwap={handleStartSlotSwap}
         />
      )}

      {/* Floating toolbar for selected page guide */}
      {selectedGuide && (() => {
        const page = pages[selectedGuide.pageIndex];
        const guide = page?.guides?.find((g) => g.id === selectedGuide.guideId);
        const container = containerRef.current;
        if (!page || !guide || !container) return null;
        const guideEl = container.querySelector(`[data-guide-id="${guide.id}"]`) as HTMLElement | null;
        if (!guideEl) return null;
        const containerRect = container.getBoundingClientRect();
        const r = guideEl.getBoundingClientRect();
        const top = r.top - containerRect.top + container.scrollTop - 40;
        const left = r.left - containerRect.left + container.scrollLeft + r.width / 2;
        return (
          <div
            className="absolute z-50 pointer-events-auto"
            style={{ top: Math.max(4, top), left, transform: 'translateX(-50%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                onDeletePageGuide?.(selectedGuide.pageIndex, selectedGuide.guideId);
                setSelectedGuide(null);
              }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-background/95 backdrop-blur-sm border border-border shadow-lg hover:bg-destructive hover:text-destructive-foreground transition-colors"
            >
              <Trash2 size={13} />
              Supprimer le repère
            </button>
          </div>
        );
      })()}

      <AddPagesDialog
        open={addPagesOpen}
        onOpenChange={setAddPagesOpen}
        totalInteriorPages={pages.filter(p => !p.coverType).length}
        defaultAfterPage={addPagesAfter}
        yearbookQuantity={yearbookQuantity ?? 0}
      />
    </div>
  );
};

export default Canvas;